// GtOutreach.js — Auto outreach (C1-OUTREACH, 3 Nov 2026).
//
// The home of c84: hiring signals into client outreach. Part 1 is the weekly hiring check: every Monday each role
// with a live Insights article is searched for job adverts from the last 7 days, and each company hiring for it
// becomes a prospect client (or updates one) with the advert saved on the client. Nobody is contacted yet.
// Parts 2 (who to send to) and 3 (sending, fully autonomous) add their tabs here as they are built.
// Campaigns may join this screen later.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthFetch } from './GtLexaAdmin';

function ukDay(v) {
  if (!v) return '';
  try { return new Date(v).toLocaleDateString('en-GB', { timeZone: 'Europe/London', weekday: 'short', day: 'numeric', month: 'short' }); } catch (e) { return ''; }
}
const plural = (n, one, many) => n + ' ' + (n === 1 ? one : many);
const host = (u) => { try { return new URL(u).hostname.replace(/^www\./, ''); } catch (e) { return ''; } };

function HiringNow({ authFetch, navigate }) {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState('');
  const [q, setQ] = useState('');
  const load = useCallback(() => authFetch('/api/insights/admin/hiring-check').then(setD).catch((e) => setMsg(e.message)), [authFetch]);
  useEffect(() => { load(); }, [load]);
  const latest = d && d.runs[0];
  const running = !!(d && (d.running || (latest && latest.status === 'running')));
  useEffect(() => { if (!running) return undefined; const t = setInterval(load, 5000); return () => clearInterval(t); }, [running, load]);

  const companies = useMemo(() => {
    if (!d) return [];
    const seen = new Map();
    const out = [];
    d.hits.forEach((h) => {
      const k = h.client_id || h.company;
      if (!seen.has(k)) { seen.set(k, { key: k, client_id: h.client_id, ref: h.client_ref, name: h.client_name || h.company, isNew: false, location: h.location, ads: [] }); out.push(seen.get(k)); }
      const c = seen.get(k);
      if (h.client_new) c.isNew = true;
      if (!c.location && h.location) c.location = h.location;
      c.ads.push(h);
    });
    const t = q.trim().toLowerCase();
    return t ? out.filter((c) => (c.name + ' ' + c.ads.map((a) => a.role_title + ' ' + (a.posting_title || '')).join(' ')).toLowerCase().includes(t)) : out;
  }, [d, q]);

  async function run() {
    setMsg('');
    try { await authFetch('/api/insights/admin/hiring-check/run', { method: 'POST' }); await load(); } catch (e) { setMsg(e.message); }
  }

  if (!d) return msg ? <div className="gtiv-card gtiv-err">{msg}</div> : <div className="gtiv-card gtiv-loading">Loading…</div>;
  const last = d.runs.find((r) => r.status === 'done');
  const shown = d.found_run || d.runs.find((r) => r.id === d.last_id);
  const roles = new Set(d.hits.map((h) => h.role_title)).size;
  return (
    <>
      <section className="gtob-hero">
        <div className="gtob-hero-t">
          <b>Hiring check</b>
          <span>Every Monday at 07:00, each role with a live Insights article is searched for job adverts from the last 7 days. Each company hiring for it is added as a prospect client, or updated if it is one already, with the advert saved on the client. The People tab then finds who to write to.</span>
        </div>
        <button type="button" className="gt-btn gt-btn--primary" disabled={running || !d.live_roles} title={!d.live_roles ? 'No articles are live yet' : ''} onClick={run}>{running ? 'Checking…' : 'Run the check now'}</button>
      </section>

      {running && (
        <div className="gtiv-card gtob-prog"><div className="gtjf-prog"><div className="gtjf-bar"><i style={{ width: (latest && latest.roles ? Math.max(5, Math.round((latest.roles_done / latest.roles) * 100)) : 5) + '%' }} /></div><span>Checking {latest ? latest.roles_done : 0} of {latest ? latest.roles : '…'} roles. You can leave this screen.</span></div></div>
      )}
      {!running && latest && latest.status === 'failed' && <div className="gtiv-card gtiv-err">The last check did not finish: {latest.error}</div>}
      {msg && <div className="gtiv-card gtiv-err">{msg}</div>}

      <div className="gtob-stats">
        <div className="gtob-stat"><em>Roles with a live article</em><b>{d.live_roles}</b></div>
        <div className="gtob-stat"><em>Adverts found</em><b>{shown ? shown.postings : 0}</b></div>
        <div className="gtob-stat"><em>Companies hiring</em><b>{shown ? shown.companies : 0}</b></div>
        <div className="gtob-stat gtob-stat--g"><em>New prospects</em><b>{shown ? shown.new_clients : 0}</b></div>
      </div>
      <p className="gtob-when">
        {last ? <>Last check {ukDay(last.finished_at || last.started_at)}{last.id !== d.last_id && d.found_at ? ', nothing new. Showing what was found ' + ukDay(d.found_at) + '.' : '.'}</> : d.live_roles ? (d.runs.length ? 'No check has finished yet.' : 'Not run yet. The first check runs next Monday, or run it now.') : 'No articles are live yet. The check starts once one is.'}
      </p>

      {companies.length > 0 || q ? (
        <>
          <div className="gtob-bar">
            <h2>{plural(companies.length, 'company', 'companies')} hiring{roles ? ' for ' + plural(roles, 'role', 'roles') + ' we have written about' : ''}</h2>
            <input className="gtiv-in gtob-search" type="search" placeholder="Search companies and roles" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search companies and roles" />
          </div>
          <ul className="gtob-list">
            {companies.map((c) => (
              <li key={c.key} className="gtob-co">
                <div className="gtob-co-h">
                  <span className="gtob-av" aria-hidden="true">{String(c.name || '?').charAt(0).toUpperCase()}</span>
                  <div className="gtob-co-t">
                    {c.client_id ? <button type="button" className="gtob-name" onClick={() => navigate('/clients/' + c.client_id)}>{c.name}</button> : <b className="gtob-name">{c.name}</b>}
                    <em>{[c.location, plural(c.ads.length, 'advert', 'adverts')].filter(Boolean).join(' · ')}</em>
                  </div>
                  {c.isNew ? <span className="gtob-chip gtob-chip--g">New prospect</span> : <span className="gtob-chip">Existing client</span>}
                </div>
                <ul className="gtob-ads">
                  {c.ads.map((a) => (
                    <li key={a.id}>
                      <div className="gtob-ad-t"><span>Hiring for</span> <b>{a.role_title}</b></div>
                      <a href={a.posting_url} target="_blank" rel="noopener noreferrer" title={a.posting_url}>{a.posting_title || 'The advert'}<i>{host(a.posting_url)} &#8599;</i></a>
                      {a.named_contact && a.named_contact.name && <div className="gtob-ad-c">Named in the advert: <b>{a.named_contact.name}</b>{a.named_contact.title ? ', ' + a.named_contact.title : ''}</div>}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
            {companies.length === 0 && <li className="gtiv-card gtob-empty">No companies match.</li>}
          </ul>
        </>
      ) : last ? <div className="gtiv-card gtob-empty">No companies found yet.</div> : null}
    </>
  );
}

// C1-PEOPLE (c84 part 2): who to send the article to at each company, and where each person came from.
const SOURCE = { advert: 'Named in the advert', gtco: 'Already in GTCO', sonar: 'Sonar', apollo: 'Apollo', web: 'Found on the web' };
// Ready means it can still be emailed: sendable and nothing written, sent or stopped yet.
const isReady = (p) => p.sendable && !p.send_status;
const personState = (p) => {
  if (p.send_status === 'replied') return ['Replied', 'g'];
  if (p.send_status === 'done' || p.send_status === 'in_progress') return [p.send_status === 'done' ? 'Emailed' : 'Being emailed', ''];
  if (p.send_status === 'bounced') return ['Bounced', 'r'];
  if (p.send_status === 'unsubscribed') return ['Unsubscribed', 'a'];
  if (p.send_status === 'stopped') return [p.stop_reason || 'Stopped', 'a'];
  return p.sendable ? ['Ready', 'g'] : [p.reason || 'Not sendable', 'a'];
};
// Companies never looked up or emailed: a web domain each. Saved straight away.
function NeverList({ authFetch }) {
  const [list, setList] = useState(null);
  const [add, setAdd] = useState('');
  const [msg, setMsg] = useState('');
  useEffect(() => { authFetch('/api/outreach/never').then((j) => setList(j.domains)).catch((e) => setMsg(e.message)); }, [authFetch]);
  async function save(next) {
    setMsg('');
    try { setList((await authFetch('/api/outreach/never', { method: 'PUT', body: JSON.stringify({ domains: next }) })).domains); setAdd(''); } catch (e) { setMsg(e.message); }
  }
  if (!list) return msg ? <div className="gtiv-card gtiv-err">{msg}</div> : null;
  return (
    <section className="gtiv-card gtob-never">
      <div className="gtob-never-h"><b>Never contact</b><span>Nobody at these companies is looked up or emailed. Type a website, or anyone's email address there.</span></div>
      <div className="gtob-never-l">
        {list.map((x) => <span key={x} className="gtob-tag">{x}<button type="button" aria-label={'Remove ' + x} onClick={() => save(list.filter((y) => y !== x))}>×</button></span>)}
      </div>
      <form className="gtob-never-f" onSubmit={(e) => { e.preventDefault(); if (add.trim()) save(list.concat(add.trim())); }}>
        <input className="gtiv-in" value={add} placeholder="company.com" onChange={(e) => setAdd(e.target.value)} aria-label="Add a company website" />
        <button type="submit" className="gt-btn" disabled={!add.trim()}>Add</button>
      </form>
      {msg && <p className="gtob-never-e">{msg}</p>}
    </section>
  );
}
function People({ authFetch, navigate }) {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState('');
  const [q, setQ] = useState('');
  const [only, setOnly] = useState('all'); // all, people (companies with someone found), ready, waiting (not yet looked at, or due another look)
  const load = useCallback(() => authFetch('/api/outreach/people').then(setD).catch((e) => setMsg(e.message)), [authFetch]);
  useEffect(() => { load(); }, [load]);
  const latest = d && d.runs[0];
  const running = !!(d && (d.running || (latest && latest.status === 'running')));
  useEffect(() => { if (!running) return undefined; const t = setInterval(load, 4000); return () => clearInterval(t); }, [running, load]);
  const list = useMemo(() => {
    if (!d) return [];
    const source = only === 'waiting' ? (d.waiting_list || []).map((w) => ({ ...w, waiting: true, note: d.companies.some((c) => c.client_id === w.client_id) ? 'Due another look, for someone who fits' : 'Not looked at yet' })) : d.companies;
    const by = new Map(source.map((c) => [c.client_id, { ...c, people: [] }]));
    d.people.forEach((p) => { const c = by.get(p.client_id); if (c && (only !== 'ready' || isReady(p))) c.people.push(p); });
    const t = q.trim().toLowerCase();
    const all = Array.from(by.values()).filter((c) => only === 'all' || only === 'waiting' || c.people.length > 0);
    return t ? all.filter((c) => (c.name + ' ' + (c.role_title || '') + ' ' + c.people.map((p) => p.full_name + ' ' + (p.title || '')).join(' ')).toLowerCase().includes(t)) : all;
  }, [d, q, only]);
  async function siteOk(id) {
    setMsg('');
    try { setD(await authFetch('/api/outreach/people/company/' + id + '/site-ok', { method: 'POST' })); } catch (e) { setMsg(e.message); }
  }
  async function run() {
    setMsg('');
    try { await authFetch('/api/outreach/people/run', { method: 'POST' }); await load(); } catch (e) { setMsg(e.message); }
  }
  if (!d) return msg ? <div className="gtiv-card gtiv-err">{msg}</div> : <div className="gtiv-card gtiv-loading">Loading…</div>;
  const sendable = d.people.filter(isReady).length;
  const card = (key, label, n, extra) => (
    <button type="button" className={'gtob-stat gtob-stat--go' + (extra || '') + (only === key ? ' gtob-stat--on' : '')} aria-pressed={only === key} onClick={() => setOnly(key)}><em>{label}</em><b>{n}</b></button>);
  return (
    <>
      <section className="gtob-hero">
        <div className="gtob-hero-t">
          <b>Who to send to</b>
          <span>After each Monday hiring check, up to 3 people at every company hiring: anyone the advert names first, then people already in GTCO, then leadership found through Apollo. Under 50 people: founders, the CTO and technical directors or heads. Over 100: talent and people leadership. 50 to 100: both. Engineering managers are only written to when the advert names them. Only verified work emails on the company's own domain, at a company whose website matches its name, and never anyone on your Never contact list.</span>
        </div>
        <button type="button" className="gt-btn gt-btn--primary" disabled={running || !d.waiting} title={!d.waiting ? 'Every company found so far has been looked at' : ''} onClick={run}>{running ? 'Looking…' : d.waiting ? (d.waiting > 40 ? 'Find people now (next 40 of ' + d.waiting + ')' : 'Find people now (' + d.waiting + ')') : 'Nothing waiting'}</button>
      </section>
      {!d.apollo && <div className="gtiv-card gtob-warn">Apollo is not set up, so only people named in adverts or already in GTCO can be found. Add APOLLO_API_KEY in Railway.</div>}
      {running && <div className="gtiv-card gtob-prog"><div className="gtjf-prog"><div className="gtjf-bar"><i style={{ width: (latest && latest.companies ? Math.max(5, Math.round((latest.done / latest.companies) * 100)) : 5) + '%' }} /></div><span>Looking at {latest ? latest.done : 0} of {latest ? latest.companies : '…'} companies. You can leave this screen.</span></div></div>}
      {msg && <div className="gtiv-card gtiv-err">{msg}</div>}
      <div className="gtob-stats">
        {card('all', 'Companies looked at', d.companies.length)}
        {card('people', 'People found', d.people.length)}
        {card('ready', 'Ready to send to', sendable, ' gtob-stat--g')}
        {card('waiting', 'Waiting to be looked at', d.waiting)}
      </div>
      <p className="gtob-when">{latest ? <>Last looked {ukDay(latest.finished_at || latest.started_at)}{latest.credits ? ', ' + plural(latest.credits, 'Apollo credit', 'Apollo credits') + ' used' : ''}{latest.error ? '. ' + latest.error : '.'}</> : 'Not run yet. It runs after the next Monday hiring check, or run it now.'}</p>
      {list.length > 0 || q ? (
        <>
          <div className="gtob-bar">
            <h2>{only === 'ready' ? plural(sendable, 'person', 'people') + ' ready to send to' : only === 'waiting' ? plural(list.length, 'company', 'companies') + ' waiting to be looked at' : plural(list.length, 'company', 'companies')}{only !== 'all' && <button type="button" className="gtiv-link gtob-showall" onClick={() => setOnly('all')}>Show all</button>}</h2>
            <input className="gtiv-in gtob-search" type="search" placeholder="Search companies and people" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search companies and people" />
          </div>
          <ul className="gtob-list">
            {list.map((c) => (
              <li key={c.client_id} className="gtob-co">
                <div className="gtob-co-h">
                  <span className="gtob-av" aria-hidden="true">{String(c.name || '?').charAt(0).toUpperCase()}</span>
                  <div className="gtob-co-t">
                    <button type="button" className="gtob-name" onClick={() => navigate('/clients/' + c.client_id)}>{c.name}</button>
                    <em>{[c.role_title ? 'Hiring for ' + c.role_title : null, plural(c.people.length, 'person', 'people'), c.waiting && c.people.length ? c.note : null, c.named ? 'named in the advert: ' + c.named : null].filter(Boolean).join(' · ')}</em>
                  </div>
                  {c.posting_url && <a className="gtob-adv" href={c.posting_url} target="_blank" rel="noopener noreferrer">The advert &#8599;</a>}
                </div>
                {c.note && /does not look like/.test(c.note) && (
                  <div className="gtob-site"><span>{c.note}</span>
                    <span className="gtob-site-a"><button type="button" className="gtiv-link" onClick={() => navigate('/clients/' + c.client_id)}>Check the client</button>
                      <button type="button" className="gt-btn" onClick={() => siteOk(c.client_id)}>It is their website</button></span></div>)}
                {c.people.length > 0 ? (
                  <ul className="gtob-ppl">
                    {c.people.map((p) => (
                      <li key={p.id}>
                        <div className="gtob-p-t"><b>{p.full_name}</b><span>{p.title || 'Title not known'}</span></div>
                        <div className="gtob-p-e">{p.email || <i>No email kept</i>}{p.linkedin_url && <a href={p.linkedin_url} target="_blank" rel="noopener noreferrer">LinkedIn &#8599;</a>}</div>
                        <span className="gtob-src">{SOURCE[p.source] || p.source}</span>
                        {(() => { const [t, tone] = personState(p); return <span className={'gtob-chip' + (tone ? ' gtob-chip--' + tone : '')} title={p.stop_reason || p.reason || ''}>{t}</span>; })()}
                      </li>
                    ))}
                  </ul>
                ) : !(c.note && /does not look like/.test(c.note)) && <p className="gtob-none">{c.note || 'Nobody found yet'}</p>}
              </li>
            ))}
            {list.length === 0 && <li className="gtiv-card gtob-empty">No companies match.</li>}
          </ul>
        </>
      ) : latest ? <div className="gtiv-card gtob-empty">No companies looked at yet.</div> : null}
      <NeverList authFetch={authFetch} />
    </>
  );
}

// C1-SEND (c84 part 3): what has been sent, and what has not and why.
function useSending(authFetch) {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState('');
  const load = useCallback(() => authFetch('/api/outreach/sending').then(setD).catch((e) => setMsg(e.message)), [authFetch]);
  useEffect(() => { load(); }, [load]);
  return { d, setD, msg, setMsg, load };
}
function when(v) { try { return new Date(v).toLocaleString('en-GB', { timeZone: 'Europe/London', weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch (e) { return ''; } }
const bodyText = (t) => String(t || '').replace(/\[\[([^\]]+)\]\]\{\{(article|library)\}\}/g, '$1');
function SendBar({ d, navigate }) {
  const mine = (d.held || []).filter((m) => m.status === 'needs_you').length;
  return (
    <>
      {!d.live && <div className="gtiv-card gtob-warn">This copy is not production, so nothing is sent from here.</div>}
      {d.live && !d.senders && <div className="gtiv-card gtob-warn">No sending address is marked for client outreach, so nothing can go. Add one in Settings, Sending.</div>}
      {d.live && !d.send_on && d.pause && <div className="gtiv-card gtob-stop"><span><b>{d.pause.kind === 'spam' ? 'Paused: spam complaints' : 'Paused: bounces'}</b>{d.pause.why}. Look at who it was on Not sent, then switch Sending outreach emails back on.</span>{navigate && <button type="button" className="gt-btn" onClick={() => navigate('/settings')}>Open Settings</button>}</div>}
      {mine > 0 && <div className="gtiv-card gtob-mine"><span>{mine === 1 ? '1 email is' : mine + ' emails are'} written and waiting for you to read before {mine === 1 ? 'it goes' : 'they go'}.</span>{navigate && <button type="button" className="gt-btn gt-btn--primary" onClick={() => navigate('/outreach/not-sent')}>Read and send</button>}</div>}
      <div className="gtob-stats">
        <div className="gtob-stat"><em>Sent today</em><b>{d.today} <small>of {d.cap}</small></b></div>
        <button type="button" className="gtob-stat gtob-stat--go" disabled={!navigate} onClick={() => navigate && navigate('/outreach/not-sent')}><em>Waiting for you</em><b>{mine}</b></button>
        <button type="button" className="gtob-stat gtob-stat--go" disabled={!navigate} onClick={() => navigate && navigate('/outreach/people')}><em>People waiting</em><b>{d.waiting}</b></button>
        <div className="gtob-stat"><em>Sending now</em><b className="gtob-small">{!d.live ? 'Not from this copy' : !d.send_on ? 'Switched off' : !d.senders ? 'No sending address' : d.in_window ? 'Yes, one every 2 to 7 min' : 'From 09:00'}</b></div>
      </div>
      <p className="gtob-when">Every day 09:00 to 17:30, one email at a time, 2 to 7 minutes apart. The daily cap starts at 20 and rises by 10 a week to 50 while the sending domain is new. Each person gets the article, then one follow-up 5 days later unless anyone at their company replies.</p>
    </>
  );
}
function Sent({ authFetch, navigate }) {
  const { d, setD, msg, setMsg } = useSending(authFetch);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState('');
  async function replied(id) {
    setBusy(id); setMsg('');
    try { setD(await authFetch('/api/outreach/sending/' + id + '/replied', { method: 'POST' })); } catch (e) { setMsg(e.message); }
    setBusy('');
  }
  if (!d) return msg ? <div className="gtiv-card gtiv-err">{msg}</div> : <div className="gtiv-card gtiv-loading">Loading…</div>;
  const state = (m) => m.send_status === 'replied' ? ['Replied', 'g'] : m.send_status === 'stopped' && m.stop_reason ? [m.stop_reason === 'Someone at the company replied' ? 'Colleague replied' : m.stop_reason, 'a'] : m.last_event === 'bounced' ? ['Bounced', 'r'] : m.last_event === 'complained' ? ['Marked as spam', 'r'] : m.send_status === 'unsubscribed' ? ['Unsubscribed', 'a'] : m.clicks > 0 ? ['Clicked', 'g'] : m.last_event === 'delivered' ? ['Delivered', ''] : ['Sent', ''];
  const clicked = d.sent.filter((m) => m.clicks > 0).length;
  const opened = (m) => (m.clicked_links || []).map((l) => l === 'article' ? 'the article' : 'the role page').join(' and ');
  return (
    <>
      <SendBar d={d} navigate={navigate} />
      {msg && <div className="gtiv-card gtiv-err">{msg}</div>}
      <div className="gtob-bar"><h2>{plural(d.sent.length, 'email', 'emails')} sent{d.sent.length ? ' · ' + clicked + ' clicked' : ''}</h2></div>
      {d.sent.length === 0 ? <div className="gtiv-card gtob-empty">Nothing sent yet.</div> : (
        <ul className="gtob-sent">
          {d.sent.map((m) => { const [st, tone] = state(m); return (
            <li key={m.id} className={open === m.id ? 'gtob-sent--open' : ''}>
              <button type="button" className="gtob-sent-row" onClick={() => setOpen(open === m.id ? null : m.id)} aria-expanded={open === m.id}>
                <span className="gtob-sent-who"><b>{m.full_name}</b><em>{m.title ? m.title + ', ' : ''}{m.company}</em></span>
                <span className="gtob-sent-s"><b>{m.subject}</b><em>{m.step > 1 ? 'Follow-up' : 'First email'} · {m.role_title}</em>{m.clicks > 0 && <em className="gtob-click">Opened {opened(m)}, {when(m.first_click_at)}{m.clicks > 1 ? ' · ' + m.clicks + ' clicks, last ' + when(m.last_click_at) : ''}</em>}</span>
                <span className="gtob-sent-t">{when(m.sent_at)}</span>
                <span className={'gtob-chip' + (tone === 'g' ? ' gtob-chip--g' : tone === 'r' ? ' gtob-chip--r' : tone === 'a' ? ' gtob-chip--a' : '')}>{st}</span>
              </button>
              {open === m.id && (
                <div className="gtob-sent-b">
                  <p className="gtob-sent-meta">From {m.from_email} to {m.to_email} · <button type="button" className="gtiv-link" onClick={() => navigate('/clients/' + m.client_id)}>Open {m.company}</button>
                    {m.send_status !== 'replied' && <> · <button type="button" className="gtiv-link" disabled={!!busy} title="A reply that only reached your own inbox: stops the follow-up and everyone at the company" onClick={() => replied(m.id)}>{busy === m.id ? 'Saving…' : 'They replied to me'}</button></>}</p>
                  <pre>{bodyText(m.body_text)}</pre>
                </div>
              )}
            </li>
          ); })}
        </ul>
      )}
    </>
  );
}
function NotSent({ authFetch, navigate }) {
  const { d, setD, msg, setMsg } = useSending(authFetch);
  const [busy, setBusy] = useState('');
  const [open, setOpen] = useState(null);
  async function act(id, a) {
    setBusy(id + a); setMsg(''); setNote('');
    setSel((p) => { if (!p.has(id)) return p; const n = new Set(p); n.delete(id); return n; }); // acted on by itself: no longer part of a selection
    try { setD(await authFetch('/api/outreach/sending/' + id + '/' + a, { method: 'POST' })); } catch (e) { setMsg(e.message); }
    setBusy('');
  }
  const [sel, setSel] = useState(() => new Set());
  const [note, setNote] = useState('');
  const pick = (id) => setSel((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  async function bulk(a) {
    const ids = d.held.filter((m) => sel.has(m.id)).map((m) => m.id); // only what is still waiting on screen
    if (!ids.length) return;
    setBusy('bulk' + a); setMsg(''); setNote('');
    try {
      const out = await authFetch('/api/outreach/sending/bulk', { method: 'POST', body: JSON.stringify({ ids, action: a }) });
      setD(out); setSel(new Set());
      const b = out.bulk || {};
      const parts = [];
      if (b.sent) parts.push(plural(b.sent, 'email', 'emails') + ' sent');
      if (b.queued) parts.push(plural(b.queued, 'email', 'emails') + ' queued: ' + (out.in_window ? 'they go one at a time, 2 to 7 minutes apart' : 'they go from 09:00, one at a time, 2 to 7 minutes apart'));
      if (b.skipped) parts.push(plural(b.skipped, 'email', 'emails') + ' not sent');
      if (b.stopped) parts.push(plural(b.stopped, 'email', 'emails') + ' stopped by the last check (see People)');
      if (b.failed) parts.push(plural(b.failed, 'email', 'emails') + ' failed to send');
      setNote((parts.length ? parts.join('. ') + '.' : 'Nothing was done.') + (b.problems && b.problems.length ? ' ' + b.problems.join(' ') : ''));
    } catch (e) { setMsg(e.message); }
    setBusy('');
  }
  async function clear(id) {
    setBusy(id ? 'p' + id : 'all'); setMsg('');
    try { setD(await authFetch('/api/outreach/not-emailed/' + (id ? id + '/' : '') + 'dismiss', { method: 'POST' })); } catch (e) { setMsg(e.message); }
    setBusy('');
  }
  if (!d) return msg ? <div className="gtiv-card gtiv-err">{msg}</div> : <div className="gtiv-card gtiv-loading">Loading…</div>;
  return (
    <>
      {msg && <div className="gtiv-card gtiv-err">{msg}</div>}
      {d.live && !d.send_on && d.pause && <div className="gtiv-card gtob-stop"><span><b>{d.pause.kind === 'spam' ? 'Paused: spam complaints' : 'Paused: bounces'}</b>{d.pause.why}. Switch Sending outreach emails back on in Settings, Automations once you have looked.</span></div>}
      {note && <div className="gtiv-card gtob-note">{note}</div>}
      <div className="gtob-bar"><h2>{plural(d.held.length, 'email', 'emails')} waiting for you</h2>
        {d.held.length > 0 && (() => { const live = d.held.filter((m) => sel.has(m.id)); const all = live.length === d.held.length; return (
          <span className="gtob-bulk">
            <label className="gtob-check"><input type="checkbox" checked={all} ref={(el) => { if (el) el.indeterminate = live.length > 0 && !all; }} onChange={() => setSel(all ? new Set() : new Set(d.held.map((m) => m.id)))} />{live.length ? live.length + ' selected' : 'Select all'}</label>
            {live.length > 0 && <>
              <button type="button" className="gt-btn gt-btn--primary" disabled={!!busy} onClick={() => bulk('send')}>{busy === 'bulksend' ? 'Sending…' : 'Send ' + live.length}</button>
              <button type="button" className="gtiv-link gtst-rm" disabled={!!busy} onClick={() => bulk('skip')}>{busy === 'bulkskip' ? 'Working…' : "Don't send " + live.length}</button>
            </>}
          </span>); })()}
      </div>
      {d.held.length === 0 ? <div className="gtiv-card gtob-empty">Nothing is waiting. Clean emails go by themselves.</div> : (
        <ul className="gtob-sent">
          {d.held.map((m) => (
            <li key={m.id} className={'gtob-sent--open' + (sel.has(m.id) ? ' gtob-picked' : '')}>
              <div className="gtob-sent-row gtob-sent-row--static gtob-sent-row--click" role="button" tabIndex={0} aria-expanded={open === m.id}
                onClick={() => setOpen(open === m.id ? null : m.id)} onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpen(open === m.id ? null : m.id); } }}>
                <span className="gtob-sent-who gtob-sent-who--pick"><input type="checkbox" aria-label={'Select ' + m.full_name} checked={sel.has(m.id)} onClick={(e) => e.stopPropagation()} onChange={() => pick(m.id)} /><span><b>{m.full_name}</b><em>{m.title ? m.title + ', ' : ''}{m.company}</em></span></span>
                <span className="gtob-sent-s"><b>{m.subject || (m.step > 1 ? 'Follow-up' : 'First email')}</b><em>{m.status === 'failed' ? m.error : (m.flags || []).map((f) => f.text).join(' · ')}</em></span>
                <span className="gtob-held-a" onClick={(e) => e.stopPropagation()}>
                  <button type="button" className="gtiv-link" onClick={() => setOpen(open === m.id ? null : m.id)}>{open === m.id ? 'Hide' : 'Read it'}</button>
                  {(m.status === 'needs_you' || /^Writing failed/.test(m.error || '')) && <button type="button" className="gt-btn" disabled={!!busy} onClick={() => act(m.id, 'rewrite')}>{busy === m.id + 'rewrite' ? 'Writing…' : 'Write again'}</button>}
                  {m.status === 'needs_you' && <button type="button" className="gt-btn gt-btn--primary" disabled={!!busy} onClick={() => act(m.id, 'send')}>Send anyway</button>}
                  {m.status === 'failed' && !/^Writing failed/.test(m.error || '') && <button type="button" className="gt-btn gt-btn--primary" disabled={!!busy} title="It cannot go twice" onClick={() => act(m.id, 'send')}>Try sending again</button>}
                  <button type="button" className="gtiv-link gtst-rm" disabled={!!busy} onClick={() => act(m.id, 'skip')}>Don't send</button>
                </span>
              </div>
              {open === m.id && <div className="gtob-sent-b"><pre>{bodyText(m.body_text) || 'Nothing written.'}</pre></div>}
            </li>
          ))}
        </ul>
      )}
      <div className="gtob-bar gtob-bar--gap"><h2>{plural(d.not_sendable.length, 'person', 'people')} not emailed</h2>
        {d.not_sendable.length > 0 && <button type="button" className="gt-btn" disabled={!!busy} onClick={() => clear(null)}>{busy === 'all' ? 'Clearing…' : 'Clear them all'}</button>}</div>
      {d.not_sendable.length === 0 ? <div className="gtiv-card gtob-empty">Nobody here.</div> : (
        <ul className="gtob-ppl gtob-ppl--flat">
          {d.not_sendable.map((p) => (
            <li key={p.id}>
              <div className="gtob-p-t"><b>{p.full_name}</b><span>{p.title || 'Title not known'}</span></div>
              <div className="gtob-p-e"><button type="button" className="gtiv-link" onClick={() => navigate('/clients/' + p.client_id)}>{p.company}</button>{p.email ? <span>{p.email}</span> : null}</div>
              <span className="gtob-src">{p.role_title}</span>
              <span className="gtob-chip gtob-chip--a" title={p.stop_reason || p.reason || ''}>{p.stop_reason || p.reason || 'Existing client'}</span>
              <button type="button" className="gtob-x" aria-label={'Clear ' + p.full_name} title="Clear from this list" disabled={!!busy} onClick={() => clear(p.id)}>×</button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export default function GtOutreach({ getToken }) {
  const authFetch = useAuthFetch(getToken);
  const navigate = useNavigate();
  const { tab } = useParams();
  const t = ['people', 'sent', 'not-sent'].includes(tab) ? tab : 'hiring';
  return (
    <div className="gtc-wrap gtob">
      <div className="gtc-head">
        <div>
          <h1 className="gtc-title">Auto outreach</h1>
          <p className="gtc-sub">Companies hiring for roles we have written about, found every week, the people there, and the article sent to them by itself.</p>
        </div>
      </div>
      <div className="gtlx-switch" role="tablist" aria-label="Auto outreach">
        <button type="button" role="tab" aria-selected={t === 'hiring'} className={'gtlx-sw' + (t === 'hiring' ? ' gtlx-sw--on' : '')} style={{ paddingLeft: 14 }} onClick={() => navigate('/outreach')}>Hiring now<em>from the weekly check</em></button>
        <button type="button" role="tab" aria-selected={t === 'people'} className={'gtlx-sw' + (t === 'people' ? ' gtlx-sw--on' : '')} style={{ paddingLeft: 14 }} onClick={() => navigate('/outreach/people')}>People<em>who to send to</em></button>
        <button type="button" role="tab" aria-selected={t === 'sent'} className={'gtlx-sw' + (t === 'sent' ? ' gtlx-sw--on' : '')} style={{ paddingLeft: 14 }} onClick={() => navigate('/outreach/sent')}>Sent<em>emails that went</em></button>
        <button type="button" role="tab" aria-selected={t === 'not-sent'} className={'gtlx-sw' + (t === 'not-sent' ? ' gtlx-sw--on' : '')} style={{ paddingLeft: 14 }} onClick={() => navigate('/outreach/not-sent')}>Not sent<em>and why</em></button>
      </div>
      {t === 'people' ? <People authFetch={authFetch} navigate={navigate} /> : t === 'sent' ? <Sent authFetch={authFetch} navigate={navigate} /> : t === 'not-sent' ? <NotSent authFetch={authFetch} navigate={navigate} /> : <HiringNow authFetch={authFetch} navigate={navigate} />}
    </div>
  );
}
